import React, { useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { changeRequestsApi } from '../services/apiService';
import { ChangeRequest } from '../types';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

interface ChangeRequestApprovalModalProps {
  visible: boolean;
  onClose: () => void;
  changeRequest: ChangeRequest | null;
  onApproved: () => void;
  onRejected: () => void;
}

const ChangeRequestApprovalModal: React.FC<ChangeRequestApprovalModalProps> = ({
  visible,
  onClose,
  changeRequest,
  onApproved,
  onRejected,
}) => {
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Get content type display using translations
  const getContentTypeDisplay = (contentType: string): string => {
    const key = `changeRequest.contentTypes.${contentType}`;
    const translated = t(key);
    // If translation returns the key itself, return the original content type
    return translated === key ? contentType : translated;
  };

  // Theme colors
  const themeColors = {
    modal: colors.card,
    border: colors.border,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    inputBackground: colors.inputBackground,
    actionsBackground: isDark ? colors.backgroundAlt : '#F9FAFB',
    infoRowBorder: isDark ? colors.border : '#F3F4F6',
    iconDefault: colors.iconDefault,
    rejectButtonBg: isDark ? colors.card : '#FFFFFF',
    cancelButtonBg: isDark ? colors.card : '#FFFFFF',
  };

  if (!changeRequest) return null;

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await changeRequestsApi.approve(changeRequest.id);
      onApproved();
      onClose();
    } catch (error) {
      console.error('Error approving:', error);
      alert(t('changeRequest.approvalError'));
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert(t('changeRequest.enterRejectReason'));
      return;
    }

    setIsRejecting(true);
    try {
      await changeRequestsApi.reject(changeRequest.id, rejectReason);
      onRejected();
      onClose();
    } catch (error) {
      console.error('Error rejecting:', error);
      alert(t('changeRequest.rejectionError'));
    } finally {
      setIsRejecting(false);
    }
  };

  const handleClose = () => {
    setShowRejectReason(false);
    setRejectReason('');
    onClose();
  };

  const isDeleteRequest = changeRequest.request_type === 'delete';
  const actionText = isDeleteRequest ? t('common.delete') : t('common.edit');
  const dateLocale = language === 'ar' ? 'ar-AE' : 'en-US';

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: themeColors.modal }]}>
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: themeColors.border }, isDeleteRequest ? styles.headerDelete : styles.headerUpdate]}>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={themeColors.iconDefault} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, isDeleteRequest ? styles.headerTitleDelete : styles.headerTitleUpdate]}>
              {t('changeRequest.request')} {actionText}
            </Text>
            <View style={[styles.iconContainer, isDeleteRequest ? styles.iconContainerDelete : styles.iconContainerUpdate]}>
              <Ionicons
                name={isDeleteRequest ? 'trash-outline' : 'create-outline'}
                size={24}
                color={isDeleteRequest ? '#EF4444' : '#F59E0B'}
              />
            </View>
          </View>

          {/* Content */}
          <ScrollView style={styles.content}>
            {/* What will be deleted/updated */}
            <View style={[styles.infoRow, { borderBottomColor: themeColors.infoRowBorder }]}>
              <Text style={[styles.infoValue, { color: themeColors.textPrimary }]}>{changeRequest.object_title}</Text>
              <Text style={[styles.infoLabel, { color: themeColors.textMuted }]}>{t('changeRequest.item')}:</Text>
            </View>

            {/* Object type */}
            <View style={[styles.infoRow, { borderBottomColor: themeColors.infoRowBorder }]}>
              <Text style={[styles.infoValue, { color: themeColors.textPrimary }]}>
                {changeRequest.content_type_display || getContentTypeDisplay(changeRequest.content_type)}
              </Text>
              <Text style={[styles.infoLabel, { color: themeColors.textMuted }]}>{t('changeRequest.type')}:</Text>
            </View>

            {/* Requested by */}
            <View style={[styles.infoRow, { borderBottomColor: themeColors.infoRowBorder }]}>
              <Text style={[styles.infoValue, { color: themeColors.textPrimary }]}>{changeRequest.requested_by_name || t('common.unknown')}</Text>
              <Text style={[styles.infoLabel, { color: themeColors.textMuted }]}>{t('changeRequest.requestedBy')}:</Text>
            </View>

            {/* Request date */}
            <View style={[styles.infoRow, { borderBottomColor: themeColors.infoRowBorder }]}>
              <Text style={[styles.infoValue, { color: themeColors.textPrimary }]}>
                {new Date(changeRequest.requested_at).toLocaleDateString(dateLocale, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Text>
              <Text style={[styles.infoLabel, { color: themeColors.textMuted }]}>{t('changeRequest.requestDate')}:</Text>
            </View>

            {/* Reason if provided */}
            {changeRequest.request_reason && (
              <View style={[styles.infoRow, { borderBottomColor: themeColors.infoRowBorder }]}>
                <Text style={[styles.infoValue, { color: themeColors.textPrimary }]}>{changeRequest.request_reason}</Text>
                <Text style={[styles.infoLabel, { color: themeColors.textMuted }]}>{t('changeRequest.reason')}:</Text>
              </View>
            )}

            {/* Warning message for delete */}
            {isDeleteRequest && (
              <View style={styles.warningBox}>
                <Ionicons name="warning-outline" size={20} color="#92400E" />
                <Text style={styles.warningText}>
                  {t('changeRequest.deleteWarning')}
                </Text>
              </View>
            )}

            {/* Cascade impact tree for delete requests */}
            {isDeleteRequest && changeRequest.cascade_impact && (
              <View style={styles.cascadeContainer}>
                <Text style={[styles.cascadeTitle, { color: themeColors.textPrimary }]}>
                  {t('deletion.cascadeImpact')}
                </Text>
                {changeRequest.cascade_impact.items?.filter(i => !i.is_root).map((item, index) => (
                  <View key={index} style={styles.cascadeItem}>
                    <Ionicons name="remove-outline" size={14} color="#EF4444" />
                    <Text style={[styles.cascadeItemText, { color: themeColors.textPrimary }]}>
                      {item.name}
                    </Text>
                    <Text style={[styles.cascadeItemType, { color: themeColors.textMuted }]}>
                      ({item.type})
                    </Text>
                  </View>
                ))}
                {changeRequest.cascade_impact.total_affected > 0 && (
                  <Text style={[styles.cascadeSummary, { color: '#EF4444' }]}>
                    {t('deletion.totalAffected')}: {changeRequest.cascade_impact.total_affected}
                  </Text>
                )}
              </View>
            )}

            {/* Reject reason input */}
            {showRejectReason && (
              <View style={styles.rejectReasonContainer}>
                <Text style={[styles.rejectReasonLabel, { color: themeColors.textPrimary }]}>{t('changeRequest.rejectReason')}:</Text>
                <TextInput
                  style={[styles.rejectReasonInput, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                  placeholder={t('changeRequest.enterRejectReasonPlaceholder')}
                  placeholderTextColor={themeColors.textMuted}
                  value={rejectReason}
                  onChangeText={setRejectReason}
                  multiline
                  numberOfLines={3}
                  textAlign={language === 'ar' ? 'right' : 'left'}
                />
              </View>
            )}
          </ScrollView>

          {/* Actions */}
          <View style={[styles.actions, { backgroundColor: themeColors.actionsBackground, borderTopColor: themeColors.border }]}>
            {!showRejectReason ? (
              <>
                {/* Reject Button */}
                <TouchableOpacity
                  style={[styles.rejectButton, { backgroundColor: themeColors.rejectButtonBg }]}
                  onPress={() => setShowRejectReason(true)}
                >
                  <Ionicons name="close-circle-outline" size={20} color="#EF4444" />
                  <Text style={styles.rejectButtonText}>{t('changeRequest.reject')}</Text>
                </TouchableOpacity>

                {/* Approve Button */}
                <TouchableOpacity
                  style={styles.approveButton}
                  onPress={handleApprove}
                  disabled={isApproving}
                >
                  {isApproving ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <>
                      <Ionicons name="checkmark-circle-outline" size={20} color="#FFFFFF" />
                      <Text style={styles.approveButtonText}>{t('changeRequest.approve')}</Text>
                    </>
                  )}
                </TouchableOpacity>
              </>
            ) : (
              <>
                {/* Cancel Reject */}
                <TouchableOpacity
                  style={[styles.cancelButton, { backgroundColor: themeColors.cancelButtonBg, borderColor: themeColors.border }]}
                  onPress={() => {
                    setShowRejectReason(false);
                    setRejectReason('');
                  }}
                >
                  <Text style={[styles.cancelButtonText, { color: themeColors.textMuted }]}>{t('common.cancel')}</Text>
                </TouchableOpacity>

                {/* Confirm Reject */}
                <TouchableOpacity
                  style={styles.confirmRejectButton}
                  onPress={handleReject}
                  disabled={isRejecting}
                >
                  {isRejecting ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <Text style={styles.confirmRejectButtonText}>{t('changeRequest.confirmReject')}</Text>
                  )}
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    maxWidth: 450,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerDelete: {
    backgroundColor: '#FEF2F2',
  },
  headerUpdate: {
    backgroundColor: '#FFFBEB',
  },
  headerTitle: {
    fontSize: 18,
    fontFamily: FONTS.bold,
  },
  headerTitleDelete: {
    color: '#991B1B',
  },
  headerTitleUpdate: {
    color: '#92400E',
  },
  closeButton: {
    padding: 4,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainerDelete: {
    backgroundColor: '#FEE2E2',
  },
  iconContainerUpdate: {
    backgroundColor: '#FEF3C7',
  },
  content: {
    padding: 20,
  },
  infoRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  infoLabel: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: FONTS.medium,
    minWidth: 80,
  },
  infoValue: {
    fontSize: 14,
    color: '#111827',
    fontFamily: FONTS.semiBold,
    textAlign: 'right',
    flex: 1,
    marginLeft: 16,
  },
  warningBox: {
    backgroundColor: '#FEF3C7',
    padding: 12,
    borderRadius: 8,
    marginTop: 16,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
  },
  warningText: {
    fontSize: 13,
    color: '#92400E',
    textAlign: 'right',
    flex: 1,
    fontFamily: FONTS.regular,
  },
  cascadeContainer: {
    marginTop: 16,
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    padding: 12,
    gap: 6,
  },
  cascadeTitle: {
    fontSize: 13,
    fontFamily: FONTS.semiBold,
    marginBottom: 4,
    textAlign: 'right',
  },
  cascadeItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 3,
  },
  cascadeItemText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    flex: 1,
    textAlign: 'right',
  },
  cascadeItemType: {
    fontSize: 11,
    fontFamily: FONTS.regular,
  },
  cascadeSummary: {
    fontSize: 12,
    fontFamily: FONTS.semiBold,
    textAlign: 'right',
    marginTop: 8,
  },
  rejectReasonContainer: {
    marginTop: 16,
  },
  rejectReasonLabel: {
    fontSize: 14,
    fontFamily: FONTS.semiBold,
    color: '#374151',
    textAlign: 'right',
    marginBottom: 8,
  },
  rejectReasonInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#F9FAFB',
    minHeight: 80,
    textAlignVertical: 'top',
    fontFamily: FONTS.regular,
  },
  actions: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    backgroundColor: '#F9FAFB',
    gap: 12,
  },
  approveButton: {
    flex: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#22C55E',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    gap: 8,
  },
  approveButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: FONTS.semiBold,
  },
  rejectButton: {
    flex: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#EF4444',
    gap: 8,
  },
  rejectButtonText: {
    color: '#EF4444',
    fontSize: 15,
    fontFamily: FONTS.semiBold,
  },
  cancelButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  cancelButtonText: {
    color: '#6B7280',
    fontSize: 15,
    fontFamily: FONTS.semiBold,
  },
  confirmRejectButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EF4444',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  confirmRejectButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: FONTS.semiBold,
  },
});

export default ChangeRequestApprovalModal;
